import { Grid, Typography } from "@material-ui/core";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import ICompany from "../../models/ICompany";
import IIntegration from "../../models/IIntegration";
import { IApplicationState } from "../../store";
import { selectAuth } from "../../store/auth/selectors";
import { selectCompany } from "../../store/context/selectors";
import { selectStatusDispatch } from "../../store/status/selectors";
import { IntegrationDetailActionRow } from "./IntegrationDetailActionRow";
import { getIntegrationFormFromModel, handleSubmit, newFormErrors } from "./functions/sharedFunctions";
import { IntegrationDiscontinueDialog } from "./dialogs/discontinue/IntegrationDiscontinueDialog";
import { FormErrors } from "../forms/FormErrors";
import SubmitForProductionDialog from "./dialogs/production/SubmitForProductionDialog";
import { IntegrationAPIDetails } from "./sections/apis/IntegrationAPIDetails";
import { IntegrationDetailSummary } from "./IntegrationDetailSummary";
import SubmitFieldsXMLDialog from "./dialogs/fields/SubmitFieldsXMLDialog";
import { IntegrationSystems } from "./IntegrationSystems";
import { getErrorsGrid } from "../../functions/errorHandling";
import { PageGrid } from "./style/IntegrationStyledComponents";
import QmlativIntegrationAccessDialog from "./dialogs/accesses/QmlativIntegrationAccessDialog";
import { SecuritySettingsDialog } from "./dialogs/security/SecuritySettingsDialog";
import { BeginDevelopmentDialog } from "./dialogs/begindevelopment/BeginDevelopmentDialog";
import { useIntegrationContext } from "./contexts/IntegrationContext";
import snackbarService from "../../services/ui-messaging/SnackbarService";

interface IntegrationDetailProps extends RouteComponentProps {
  token: string;
  username: string;
  company?: ICompany;
  addPending: Function;
  completePending: Function;
  readonly: boolean;
  model: IIntegration;
}

const IntegrationDetail = (props: IntegrationDetailProps) => {
  const { token, username, company, addPending, completePending, model } = props;
  const [form, setForm] = React.useState<IIntegration>(getIntegrationFormFromModel(model));
  const [showBeginDevelopmentDialog, setShowBeginDevelopmentDialog] = React.useState<boolean>(false);
  const [showSecuritySettingsDialog, setShowSecuritySettingsDialog] = React.useState<boolean>(false);
  const [showDiscontinueDialog, setShowDiscontinueDialog] = React.useState<boolean>(false);
  const [allowEdit, setAllowEdit] = React.useState<boolean>(false);
  const [integrationErrors, setIntegrationErrors] = React.useState<string[]>([""]);
  const [formErrors, setFormErrors] = React.useState<FormErrors<IIntegration>>(newFormErrors());
  const [showQmlativAccess, setShowQmlativAccess] = React.useState<boolean>(false);

  const { setShowSubmitForProductionDialog } = useIntegrationContext();

  const handleBeginDevelopmentSuccess = () => {
    window.location.reload();
  };

  const handleDiscontinue = (discontinueForm: IIntegration) => {
    handleSubmit({ form: discontinueForm, token, company, addPending, completePending, setFormErrors, setIntegrationErrors, setAllowEdit });
    setForm(discontinueForm);
  };

  const cancelEdit = () => {
    setAllowEdit(false);
    setIntegrationErrors([""]);
    setForm(getIntegrationFormFromModel(model));
  };

  const handleSave = () => {
    setIntegrationErrors([""]);
    setFormErrors(newFormErrors());
    handleSubmit({ form, token, company, addPending, completePending, setFormErrors, setIntegrationErrors, setAllowEdit });
  };

  return (
    <PageGrid container spacing={2}>
      {form && form.ID > 0 && (
        <Grid item xs={12}>
          <BeginDevelopmentDialog
            showDialog={showBeginDevelopmentDialog}
            token={token}
            integration={form}
            onClose={() => setShowBeginDevelopmentDialog(false)}
            onSuccess={handleBeginDevelopmentSuccess}
          />
          <SecuritySettingsDialog token={token} showDialog={showSecuritySettingsDialog} id={form.ID} onClose={() => setShowSecuritySettingsDialog(false)} />
          <IntegrationDiscontinueDialog
            showDialog={showDiscontinueDialog}
            setShowDialog={setShowDiscontinueDialog}
            form={form}
            handleSubmit={handleDiscontinue}
          />
          <SubmitForProductionDialog integration={form} token={token} username={username} setForm={setForm} setFormErrors={setFormErrors} />
          <SubmitFieldsXMLDialog integrationID={form.ID} integrationName={form.Name} />
          <QmlativIntegrationAccessDialog integrationID={form.ID} token={token} showDialog={showQmlativAccess} onClose={() => setShowQmlativAccess(false)} />
        </Grid>
      )}
      <Grid item xs={12}>
        <Typography component="div" variant="h4">
          {form.Name}
        </Typography>
      </Grid>
      <Grid container item spacing={2} xs={12}>
        <Grid item xs={10}>
          <Grid item xs={12}>
            <IntegrationDetailSummary
              form={form}
              formErrors={formErrors}
              allowEdit={allowEdit}
              setAllowEdit={setAllowEdit}
              setForm={setForm}
              cancelEdit={cancelEdit}
              handleSave={handleSave}
            />
          </Grid>
          {integrationErrors && integrationErrors.length > 0 && (
            <>
              <Grid item xs={12}>
                {getErrorsGrid(integrationErrors)}
              </Grid>
            </>
          )}
        </Grid>
        <Grid item xs={2}>
          <IntegrationDetailActionRow
            handleBeginDevelopment={() => setShowBeginDevelopmentDialog(true)}
            handleSecuritySettings={() => setShowSecuritySettingsDialog(true)}
            handleSubmitForProduction={() => setShowSubmitForProductionDialog(true)}
            handleDiscontinue={() => setShowDiscontinueDialog(true)}
            handleQmlativAccess={() => setShowQmlativAccess(true)}
            isQmlativ={form.System.ID === IntegrationSystems.Qmlativ}
            integrationStatusID={form.Status.ID}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <IntegrationAPIDetails token={token} username={username} integration={form} setIntegrationErrors={setIntegrationErrors} />
      </Grid>
    </PageGrid>
  );
};

const FormWithState = connect(
  (state: IApplicationState) => ({
    ...selectAuth(state),
    company: selectCompany(state),
  }),
  dispatch => selectStatusDispatch(dispatch),
)(IntegrationDetail);

export default withRouter(FormWithState);
